import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";

type AuthSuccessContentProps = {
  success?: boolean;
  children: ReactNode;
  contentTestId?: string;
  indicatorTestId?: string;
};

export function AuthSuccessIndicator({
  testId = "auth-success-indicator",
}: {
  testId?: string;
}) {
  const theme = useTheme();
  const successColor = theme.palette.success.main;

  return (
    <Box
      data-testid={testId}
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          bgcolor: alpha(successColor, 0.1),
          p: 1.25,
          "@keyframes authSuccessCheckPop": {
            "0%": {
              opacity: 0,
              transform: "scale(0.82)",
            },
            "100%": {
              opacity: 1,
              transform: "scale(1)",
            },
          },
          animation: "authSuccessCheckPop 0.38s ease-out",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <CheckCircleOutlineIcon
          sx={{
            fontSize: 44,
            color: successColor,
          }}
        />
      </Box>
    </Box>
  );
}

export function AuthSuccessContent({
  success = false,
  children,
  contentTestId,
  indicatorTestId,
}: AuthSuccessContentProps) {
  return (
    <Box sx={{ position: "relative" }} data-testid={contentTestId}>
      <Box
        sx={{
          transition: "opacity 0.32s ease",
          opacity: success ? 0.3 : 1,
          pointerEvents: success ? "none" : "auto",
        }}
      >
        {children}
      </Box>
      {success ? <AuthSuccessIndicator testId={indicatorTestId} /> : null}
    </Box>
  );
}
